import { useEffect, useRef, useState } from 'react';

export function useHorizontalScrollIndicators<T extends HTMLElement>() {
  const containerRef = useRef<T>(null);
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);
  const scrollIncrement = 200;
  // Small tolerance for sub-pixel rounding and fractional zoom.
  // Without this, `scrollLeft + clientWidth` can be < `scrollWidth` by a fraction,
  // leaving the right indicator visible even when scrolled fully to the end.
  const EPSILON = 2; // tolerance for sub-pixel rounding

  const handleScrollLeft = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft - scrollIncrement, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft + scrollIncrement, behavior: 'smooth' });
  };

  const checkForOverflow = () => {
    const el = containerRef.current;
    if (!el) return;

    // Compute how far we are from each edge and use EPSILON to avoid off-by-one
    // issues at the boundaries due to rounding and scrollbar behavior.
    const maxScrollableLeft = el.scrollWidth - el.clientWidth;
    const distanceFromLeft = el.scrollLeft;
    const distanceFromRight = maxScrollableLeft - el.scrollLeft;

    setShowLeftIndicator(distanceFromLeft > EPSILON);
    setShowRightIndicator(distanceFromRight > EPSILON);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    checkForOverflow();

    const onScroll = () => checkForOverflow();
    const onResize = () => checkForOverflow();

    window.addEventListener('resize', onResize);
    el.addEventListener('scroll', onScroll, { passive: true });

    // Recompute indicators when the container size or its children change.
    // Without these observers, dynamic UI updates (e.g., content mounts/unmounts,
    // font loading, or wrapping changes) can leave the indicators in an incorrect state.
    const resizeObserver = new ResizeObserver(checkForOverflow);
    resizeObserver.observe(el);

    const mutationObserver = new MutationObserver(checkForOverflow);
    mutationObserver.observe(el, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', onResize);
      el.removeEventListener('scroll', onScroll as EventListener);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    showLeftIndicator,
    showRightIndicator,
    handleScrollLeft,
    handleScrollRight,
    checkForOverflow,
  };
}
